import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Modal,
  Table,
  Badge,
  Alert,
  Form,
  Spinner,
} from "react-bootstrap";
import {
  FaMoneyCheck,
  FaEye,
  FaCheck,
  FaTimes,
  FaUser,
  FaBuilding,
  FaFileExcel,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const WithdrawalRequests = () => {
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [allWithdrawals, setAllWithdrawals] = useState([]); // Store all withdrawals
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    completed: 0,
    rejected: 0,
    all: 0,
  });
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(""); // 'completed', 'rejected'
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllWithdrawals();
  }, []);

  useEffect(() => {
    // Filter withdrawals whenever filterStatus changes
    filterWithdrawals();
    updateStatusCounts();
  }, [filterStatus, allWithdrawals]);

  const fetchAllWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let withdrawalData = null;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          console.log("Fetching from base:", base);
          const url = `${base}/api/withdrawals?limit=100`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 6000);
          const withdrawalRes = await fetch(url, { headers, signal: controller.signal });
          clearTimeout(timeoutId);

          if (!withdrawalRes.ok) {
            console.error(
              `Failed to fetch from ${base}:`,
              withdrawalRes.status
            );
            lastErr = new Error(`Failed to fetch from ${base}`);
            continue;
          }

          withdrawalData = await withdrawalRes.json();
          console.log("Raw API response:", withdrawalData);
          break;
        } catch (e) {
          console.error("Fetch error for base:", base, e);
          lastErr = e;
          continue;
        }
      }

      if (!withdrawalData) {
        throw (
          lastErr ||
          new Error("Failed to load withdrawal data from all endpoints")
        );
      }

      // Handle different possible response structures
      let withdrawalsArray = [];
      if (Array.isArray(withdrawalData.withdrawals)) {
        withdrawalsArray = withdrawalData.withdrawals;
      } else if (Array.isArray(withdrawalData)) {
        withdrawalsArray = withdrawalData;
      } else if (withdrawalData.data && Array.isArray(withdrawalData.data)) {
        withdrawalsArray = withdrawalData.data;
      }

      console.log("Processed withdrawals:", withdrawalsArray);
      setAllWithdrawals(withdrawalsArray);
    } catch (error) {
      console.error("Error fetching withdrawal requests:", error);
      setError("Failed to load withdrawal requests: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterWithdrawals = () => {
    if (filterStatus === "all") {
      // Filter out 'failed' requests even when showing 'all' requests
      const filtered = allWithdrawals.filter((withdrawal) => {
        return withdrawal.status !== "failed";
      });
      setWithdrawalRequests(filtered);
    } else {
      // Filter by the exact status
      const filtered = allWithdrawals.filter((withdrawal) => {
        return withdrawal.status === filterStatus;
      });
      console.log(`Filtered ${filterStatus} withdrawals:`, filtered);
      setWithdrawalRequests(filtered);
    }
  };

  // Update status counts
  const updateStatusCounts = () => {
    if (allWithdrawals.length === 0) return;

    // Count all non-failed requests for the 'all' filter
    const allCount = allWithdrawals.filter((w) => w.status !== "failed").length;

    const counts = {
      pending: allWithdrawals.filter((w) => w.status === "pending").length,
      completed: allWithdrawals.filter((w) => w.status === "completed").length,
      rejected: allWithdrawals.filter((w) => w.status === "rejected").length,
      all: allCount, // Add all count (excluding failed)
    };

    console.log("Status counts:", counts);
    setStatusCounts(counts);
  };

  const handleViewDetails = (withdrawalRequest) => {
    setSelectedWithdrawal(withdrawalRequest);
    setShowDetailModal(true);
  };

  const handleAction = (withdrawalRequest, action) => {
    setSelectedWithdrawal(withdrawalRequest);
    setActionType(action);
    setRejectionReason("");
    setShowActionModal(true);
  };

  const processAction = async () => {
    if (!selectedWithdrawal) return;

    if (actionType === "rejected" && !rejectionReason.trim()) {
      alert("Please provide a reason");
      return;
    }

    try {
      setActionLoading(true);

      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      let success = false;
      let lastErr = null;

      for (const base of bases) {
        try {
          const adminToken =
            localStorage.getItem("adminToken") ||
            sessionStorage.getItem("adminToken");
          const endpoint = `${base}/api/withdrawals/${selectedWithdrawal.id}/status`;

          const body = {
            status: actionType,
            processedBy: adminToken || "admin",
          };

          if (actionType === "rejected") {
            body.rejectionReason = rejectionReason.trim();
          }

          const headers = {
            "Content-Type": "application/json",
          };
          if (adminToken) {
            headers["Authorization"] = `Bearer ${adminToken}`;
          }

          console.log("Updating withdrawal status:", endpoint, body);
          const response = await fetch(endpoint, {
            method: "PUT",
            headers,
            body: JSON.stringify(body),
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.message || `Failed to ${actionType} withdrawal`
            );
          }

          success = true;
          break;
        } catch (e) {
          console.error("Error updating withdrawal status:", e);
          lastErr = e;
          continue;
        }
      }

      if (!success) {
        throw lastErr || new Error(`Failed to ${actionType} withdrawal`);
      }

      // Refresh all data
      await fetchAllWithdrawals();

      setShowActionModal(false);
      setSelectedWithdrawal(null);

      // Show appropriate message
      alert(`Withdrawal ${actionType} successfully`);
    } catch (error) {
      console.error(`Error ${actionType}ing withdrawal:`, error);
      alert(`Failed to ${actionType} withdrawal: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "warning", icon: FaMoneyCheck, text: "Pending" },
      completed: { variant: "success", icon: FaCheck, text: "Completed" },
      rejected: { variant: "danger", icon: FaTimes, text: "Rejected" },
    };

    const config = statusConfig[status] || {
      variant: "secondary",
      icon: FaMoneyCheck,
      text: status,
    };
    const IconComponent = config.icon;

    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        <IconComponent size={12} />
        {config.text}
      </Badge>
    );
  };

  const getPaymentMethodBadge = (method) => {
    if (method === "bank_transfer") {
      return <Badge bg="primary">Bank Transfer</Badge>;
    } else if (method === "upi") {
      return <Badge bg="success">UPI</Badge>;
    } else {
      return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const exportToExcel = () => {
    // Prepare data for export
    const dataToExport = allWithdrawals.map((withdrawal) => ({
      "Withdrawal ID": withdrawal.id || "",
      "Vendor ID": withdrawal.vendorId || "",
      Amount: withdrawal.amount || 0,
      "Payment Method": withdrawal.paymentMethod || "",
      Status: withdrawal.status || "",
      "Requested Date": withdrawal.createdAt
        ? new Date(withdrawal.createdAt).toLocaleString()
        : "",
      "Payment Details": withdrawal.paymentDetails || "",
      "Rejection Reason": withdrawal.rejectionReason || "",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Withdrawal Requests");

    // Export to Excel
    XLSX.writeFile(wb, "withdrawal_requests.xlsx");
  };

  // Debug component to see what's happening

  if (loading && allWithdrawals.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading withdrawal requests...</span>
      </div>
    );
  }

  if (error && allWithdrawals.length === 0) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchAllWithdrawals}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <h2 className="mb-0 fw-bold">
          <FaMoneyCheck className="me-2 text-primary" />
          Withdrawal Requests
        </h2>
        <Button variant="outline-secondary" size="sm" onClick={() => navigate("/")}>
          Back to Dashboard
        </Button>
      </div>

      {/* Filter Tabs */}
      <Card className="mb-4 shadow-sm border-0">
        <Card.Header className="bg-light border-0 py-3">
          <div className="d-flex gap-2 flex-wrap">
            {["all", "pending", "completed", "rejected"].map((status) => (
              <Button
                key={status}
                variant={
                  filterStatus === status ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {status === "all"
                  ? "All"
                  : status.charAt(0).toUpperCase() + status.slice(1)}
                <Badge bg="light" text="dark" className="ms-2">
                  {status === "all"
                    ? statusCounts.all || 0
                    : statusCounts[status] || 0}
                </Badge>
              </Button>
            ))}
          </div>
        </Card.Header>
      </Card>

      {/* Withdrawal Requests Table */}
      <Card className="shadow-sm border-0">
        <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 bg-light border-0 py-3">
          <h5 className="mb-0 fw-bold">
            {filterStatus === "all"
              ? "All"
              : filterStatus.charAt(0).toUpperCase() +
              filterStatus.slice(1)}{" "}
            Withdrawal Requests
            <Badge bg="secondary" className="ms-2">
              {withdrawalRequests.length}
            </Badge>
          </h5>
          <div className="d-flex gap-2">
            <Button variant="outline-success" size="sm" onClick={exportToExcel}>
              <FaFileExcel className="me-1" />
              Export to Excel
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={fetchAllWithdrawals}
            >
              Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Loading withdrawal requests...</span>
            </div>
          ) : withdrawalRequests.length === 0 ? (
            <div className="text-center py-5">
              <FaMoneyCheck size={48} className="text-muted mb-3" />
              <h5 className="text-muted">
                No {filterStatus} withdrawal requests found
              </h5>
              <p className="text-muted">
                {filterStatus === "all"
                  ? "There are currently no withdrawal requests."
                  : `There are currently no withdrawal requests with ${filterStatus} status.`}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Vendor ID</th>
                    <th>Amount</th>
                    <th>Payment Method</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawalRequests.map((withdrawal) => (
                    <tr key={withdrawal.id}>
                      <td>
                        <small className="text-muted fw-semibold">
                          {withdrawal.id}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaUser className="text-muted me-2" />
                          <div>
                            <div className="fw-semibold">
                              {withdrawal.vendorName || withdrawal.vendorId?.substring(0, 8) + "..."}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <strong>₹{withdrawal.amount}</strong>
                      </td>
                      <td>{getPaymentMethodBadge(withdrawal.paymentMethod)}</td>
                      <td>{getStatusBadge(withdrawal.status)}</td>
                      <td>
                        <small>
                          {withdrawal.createdAt
                            ? new Date(
                              withdrawal.createdAt
                            ).toLocaleDateString()
                            : "N/A"}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(withdrawal)}
                          >
                            <FaEye size={12} className="me-1" />
                            View
                          </Button>
                          {withdrawal.status === "pending" && (
                            <>
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() =>
                                  handleAction(withdrawal, "completed")
                                }
                              >
                                <FaCheck size={12} className="me-1" />
                                Complete
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleAction(withdrawal, "rejected")
                                }
                              >
                                <FaTimes size={12} className="me-1" />
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaMoneyCheck className="me-2" />
            Withdrawal Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedWithdrawal && (
            <Row>
              <Col md={8}>
                <Card className="mb-3">
                  <Card.Header>
                    <FaUser className="me-2" />
                    Withdrawal Information
                  </Card.Header>
                  <Card.Body>
                    <p>
                      <strong>Withdrawal ID:</strong> {selectedWithdrawal.id}
                    </p>
                    <p><strong>Vendor:</strong> {selectedWithdrawal.vendorName || "Unknown Vendor"}</p>
                    <p><strong>Vendor ID:</strong> {selectedWithdrawal.vendorId}</p>
                    <p>
                      <strong>Amount:</strong>{" "}
                      <strong>₹{selectedWithdrawal.amount}</strong>
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      {getStatusBadge(selectedWithdrawal.status)}
                    </p>
                    <p>
                      <strong>Payment Method:</strong>{" "}
                      {getPaymentMethodBadge(selectedWithdrawal.paymentMethod)}
                    </p>
                    <p>
                      <strong>Requested:</strong>{" "}
                      {selectedWithdrawal.createdAt
                        ? new Date(
                          selectedWithdrawal.createdAt
                        ).toLocaleString()
                        : "N/A"}
                    </p>
                    {selectedWithdrawal.processedAt && (
                      <p>
                        <strong>Processed:</strong>{" "}
                        {new Date(
                          selectedWithdrawal.processedAt
                        ).toLocaleString()}
                      </p>
                    )}
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <FaBuilding className="me-2" />
                    Payment Details
                  </Card.Header>
                  <Card.Body>
                    <p>
                      <strong>Payment Details:</strong>
                    </p>
                    <pre
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedWithdrawal.paymentDetails ||
                        "No payment details available"}
                    </pre>

                    {selectedWithdrawal.notes && (
                      <div className="mt-3">
                        <p>
                          <strong>Notes:</strong>
                        </p>
                        <p>{selectedWithdrawal.notes}</p>
                      </div>
                    )}

                    {selectedWithdrawal.rejectionReason && (
                      <Alert variant="warning" className="mt-3">
                        <strong>Rejection Reason:</strong>
                        <br />
                        {selectedWithdrawal.rejectionReason}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          {selectedWithdrawal?.status === "pending" && (
            <>
              <Button
                variant="success"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedWithdrawal, "completed");
                }}
              >
                <FaCheck className="me-1" />
                Complete
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowDetailModal(false);
                  handleAction(selectedWithdrawal, "rejected");
                }}
              >
                <FaTimes className="me-1" />
                Reject
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal
        show={showActionModal}
        onHide={() => setShowActionModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "completed" ? (
              <>
                <FaCheck className="me-2 text-success" />
                Complete Withdrawal
              </>
            ) : (
              <>
                <FaTimes className="me-2 text-danger" />
                Reject Withdrawal
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {actionType} the withdrawal request for{" "}
            <strong>{selectedWithdrawal?.vendorId?.substring(0, 8)}...</strong>?
          </p>
          <p>
            <strong>Amount:</strong> ₹{selectedWithdrawal?.amount}
          </p>

          {actionType === "rejected" && (
            <Form.Group className="mt-3">
              <Form.Label>Reason *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a detailed reason..."
                required
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowActionModal(false)}>
            Cancel
          </Button>
          <Button
            variant={actionType === "rejected" ? "danger" : "success"}
            onClick={processAction}
            disabled={
              actionLoading ||
              (actionType === "rejected" && !rejectionReason.trim())
            }
          >
            {actionLoading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : actionType === "rejected" ? (
              <FaTimes className="me-1" />
            ) : (
              <FaCheck className="me-1" />
            )}
            Confirm {actionType === "rejected" ? "Rejection" : "Completion"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .withdrawal-requests-container {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        .table th {
          border-top: none;
          font-weight: 600;
          background-color: #f8f9fa;
        }
        .badge {
          font-size: 0.8em;
        }
      `}</style>
    </Container>
  );
};

export default WithdrawalRequests;
